<template>
    <div>
        <sonicu-header>Alarms</sonicu-header>
        <div v-if="loadError" class="mt-3 px-4 py-3 text-sm text-red-700 bg-red-50 border border-red-200 rounded">
            {{ loadErrorMessage }}
        </div>
        <div class="mt-3">
            <!-- Column-aligned filters: rendered on the same 6-column grid the table uses, so each
                 select2 sits over its column (Site → Device, Type, Alarm Profile, Status). The
                 Last Action / Start columns aren't filterable, so their two cells stay empty. -->
            <div class="grid grid-cols-6 mb-3">
                <div v-for="filter in filterConfig" :key="filter.key" class="px-4 sm:px-6">
                    <label class="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">
                        {{ filter.label }}
                    </label>
                    <select2
                        :allow-clear="true"
                        :close-on-select="true"
                        :multiple="true"
                        :options="select2Options[filter.key]"
                        :value="filters[filter.key]"
                        placeholder="All"
                        width="100"
                        @update:value="(val) => onFilterChange(filter.key, val)"
                    ></select2>
                </div>
            </div>
            <ajax-table
                v-model:items-per-page="ajaxTable.itemsPerPage"
                v-model:loading="ajaxTable.loading"
                v-model:page="ajaxTable.page"
                v-model:page-info="ajaxTable.pageInfo"
                v-model:search="ajaxTable.search"
                v-model:sort-by="ajaxTable.sortBy"
                v-model:sort-desc="ajaxTable.sortDesc"
                :data="data"
                :exportable="false"
                :headers="headers"
                :search-id="`enterprise-alarm-search-${regionId}`"
                no-data-message="No alarms in this period."
                @loadData="fetchRegionAlarms"
            >
                <!-- Device (with its site/location beneath) -->
                <template #col-1="props">
                    <div    class="flex flex-col">
                        <div class="text-sm font-medium text-gray-900">{{ props.row.device }}</div>
                        <div v-if="props.row.location" class="text-xs italic text-gray-400">{{ props.row.location }}</div>
                    </div>
                </template>

                <!-- Type -->
                <template #col-2="props">
                    <span class="text-sm text-gray-700">{{ props.row.type }}</span>
                </template>

                <!-- Alarm Profile -->
                <template #col-3="props">
                    <span class="text-sm text-gray-700">{{ props.row.profile }}</span>
                </template>

                <!-- Status -->
                <template #col-4="props">
                    <span :class="statusClasses(props.row.status)"
                          class="inline-flex px-2 text-xs font-semibold leading-5 rounded-full">{{ props.row.status }}</span>
                </template>

                <!-- Last Action -->
                <template #col-5="props">
                    <span class="text-sm text-gray-700">{{ props.row.lastAction }}</span>
                </template>

                <!-- Start (with duration in parentheses) -->
                <template #col-6="props">
                    <span class="text-sm text-gray-700">
                        {{ props.row.start }}
                        <span class="text-gray-400">({{ props.row.duration }})</span>
                    </span>
                </template>
            </ajax-table>
        </div>
    </div>
</template>

<script>
import {SonicuCoreMixin} from "@/mixins/SonicuCoreMixin";
import {iconNamesMixin} from "@/mixins/IconNamesMixin";
import {ajaxTableMixin} from "@/mixins/ajaxTableMixin";
import AjaxTable from "@/components/elements/AjaxTable";
import Select2 from "@/components/elements/select2.vue";
import axios from "axios";
import _ from "lodash";

export default {
    name: "RegionAlarmTable",
    // SonicuCoreMixin is included here (unlike the other dashboard child components) because
    // this table self-fetches its own paginated data and needs this.company to build the route.
    mixins: [SonicuCoreMixin, iconNamesMixin, ajaxTableMixin],
    components: {AjaxTable, Select2},
    props: {
        companyId: {
            type: String,
            default: "",
        },
        regionId: {
            type: String,
            required: true,
        },
        // yyyy-MM-dd date-range bounds, mirrored from the dashboard shell.
        start: {
            type: String,
            default: "",
        },
        end: {
            type: String,
            default: "",
        },
    },
    data() {
        return {
            data: [],
            headers: [
                {name: "Device", key: "device", sortBy: "device"},
                {name: "Type", key: "type", sortBy: "type"},
                {name: "Alarm Profile", key: "profile", sortBy: "profile"},
                {name: "Status", key: "status", sortBy: "status"},
                {name: "Last Action", key: "lastAction", sortBy: "lastAction"},
                {name: "Start", key: "start", sortBy: "start"},
            ],
            requestNumber: 0,
            loadError: false,
            loadErrorMessage: "",
            // Exact-match dropdown filters, mirrored to the region-alarms request params (keys match
            // the row/param names the controller filters on). Each is a list of selected values —
            // a row passes when it matches ANY of them. [] = no filter for that column.
            filters: {
                location: [],
                type: [],
                profile: [],
                status: [],
            },
            // Available values per filter, supplied by the endpoint (computed from the full region
            // list, so they stay complete regardless of the current selection).
            filterOptions: {
                location: [],
                type: [],
                profile: [],
                status: [],
            },
            filterConfig: [
                {key: "location", label: "Site"},
                {key: "type", label: "Type"},
                {key: "profile", label: "Alarm Profile"},
                {key: "status", label: "Status"},
            ],
        };
    },
    created() {
        // Default to showing 10 alarms at a time (the mixin defaults to 100). Set here rather than
        // forced onto the request so the table's "Items per page" selector actually takes effect.
        this.ajaxTable.itemsPerPage = "10";
    },
    computed: {
        // Select2 takes a { value: label } map. No empty ("All") entry: an unset filter is the
        // empty list, which select2 renders as the "All" placeholder (see the template) and
        // clears via allow-clear. Rebuilt from the endpoint-supplied option lists.
        select2Options() {
            const build = (values) => {
                const map = {};
                (values || []).forEach((value) => {
                    map[value] = value;
                });
                return map;
            };
            return {
                location: build(this.filterOptions.location),
                type: build(this.filterOptions.type),
                profile: build(this.filterOptions.profile),
                status: build(this.filterOptions.status),
            };
        },
    },
    watch: {
        // Refetch when the dashboard's date range changes; reset to the first page and clear filters
        // (a value selected for one window may not exist in the next).
        start() {
            this.ajaxTable.page = 1;
            this.resetFilters();
            this.fetchRegionAlarms();
        },
        end() {
            this.ajaxTable.page = 1;
            this.resetFilters();
            this.fetchRegionAlarms();
        },
    },
    methods: {
        onFilterChange(key, value) {
            // A multiple select2 emits an array of the selected values, or null once cleared.
            const normalized = Array.isArray(value) ? value : (_.isNil(value) || value === "" ? [] : [value]);
            // Select2 emits a change on every (re)build, not just on user input — only react when the
            // value actually changed, otherwise refetching would loop as the options reload.
            if (_.isEqual(this.filters[key], normalized)) {
                return;
            }
            this.filters[key] = normalized;
            this.ajaxTable.page = 1;
            this.fetchRegionAlarms();
        },
        resetFilters() {
            this.filters = {location: [], type: [], profile: [], status: []};
        },
        statusClasses(status) {
            switch (status) {
                case "Active":
                    return "bg-red-100 text-red-800";
                case "Snoozed":
                    return "bg-yellow-100 text-yellow-800";
                case "Resolved":
                    return "bg-green-100 text-green-800";
                default:
                    return "bg-gray-100 text-gray-800";
            }
        },
        fetchRegionAlarms() {
            this.ajaxTable.loading = true;
            this.loadError = false;
            this.requestNumber++;
            // Each call closes over its own snapshot of the request number, while this.requestNumber
            // advances to the newest request; so a callback seeing requestNumber < this.requestNumber
            // knows a newer request superseded it. Snapshotting (vs. reading meta.requestNumber off
            // the response) is what lets the catch detect staleness even when an error carries no body.
            const requestNumber = this.requestNumber;

            const params = JSON.parse(JSON.stringify(this.ajaxTable));
            params.requestNumber = requestNumber;
            params.region = this.regionId;
            params.start = this.start;
            params.end = this.end;
            // Exact-match column filters, each a list of accepted values (empty = unfiltered).
            // axios serializes these as `location[]=A&location[]=B`, which Laravel reads as arrays.
            params.location = this.filters.location;
            params.type = this.filters.type;
            params.profile = this.filters.profile;
            params.status = this.filters.status;

            const url = this.company.buildCompanyApiInternalRoute(
                "enterprise-alarm-dashboard/region-alarms",
                this.companyId
            );

            axios
                .get(url, {params})
                .then((response) => {
                    const payload = response.data;
                    // Ignore stale responses that resolve out of order.
                    if (payload?.meta?.requestNumber < this.requestNumber) {
                        return;
                    }
                    this.ajaxUpdateTableData(payload);
                    this.data = !_.isNil(payload.data) ? payload.data : [];
                    // Only replace when the option set actually changed, so the select2s don't
                    // rebuild (and briefly flicker) on every pagination/sort request.
                    if (payload?.meta?.filterOptions
                        && JSON.stringify(payload.meta.filterOptions) !== JSON.stringify(this.filterOptions)) {
                        this.filterOptions = payload.meta.filterOptions;
                    }
                })
                .catch((error) => {
                    // A newer request has since gone out — don't let this stale rejection surface an
                    // error banner (or wipe the table) over the fresher request's result.
                    if (requestNumber < this.requestNumber) {
                        return;
                    }
                    this.loadError = true;
                    this.loadErrorMessage = error.response?.data?.error
                        ?? "Failed to load alarms. Please try again.";
                    this.data = [];
                })
                .finally(() => {
                    this.ajaxTable.loading = false;
                });
        },
    },
};
</script>

<style scoped>
</style>
